import { useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet, X, CheckCircle2, AlertTriangle, SkipForward, Plus, Pencil, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const STATUS_META = {
  create: { label: "Nuovo", icon: Plus, cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  update: { label: "Aggiorna", icon: Pencil, cls: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300" },
  duplicate_skipped: { label: "Duplicato", icon: SkipForward, cls: "bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300" },
  error: { label: "Errore", icon: AlertTriangle, cls: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300" },
  error_permission_denied: { label: "Permesso", icon: AlertTriangle, cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
};

export default function ImportExcelModal({ entity, onClose, onComplete, hotelId, hotelNome }) {
  // entity: "hotels" | "staff" | "rooms" | "rooms-bulk"
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("insert_only");
  const [previewData, setPreviewData] = useState(null);
  const [executeData, setExecuteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const inputRef = useRef(null);

  const isHotels = entity === "hotels";
  const isRooms = entity === "rooms";
  const isRoomsBulk = entity === "rooms-bulk";
  const isHostfix = entity === "hostfix-csv";
  const title = isHotels
    ? "Importa Hotel da Excel"
    : isHostfix
      ? "Importa da HostFix (CSV)"
      : isRoomsBulk
        ? "Importa camere multi-hotel"
        : isRooms
          ? `Importa camere · ${hotelNome || ""}`
          : "Importa Staff da Excel";

  const basePath = isRooms
    ? `/import/hotels/${hotelId}/rooms`
    : isRoomsBulk
      ? `/import/rooms-bulk`
      : isHostfix
        ? `/import/hostfix-csv`
        : `/import/${entity}`;
  const downloadName = isHotels
    ? "template-hotel.xlsx"
    : isRooms
      ? "template-camere.xlsx"
      : isRoomsBulk
        ? "template-camere-multi-hotel.xlsx"
        : isHostfix
          ? "template-hostfix.csv"
          : "template-staff.xlsx";
  const acceptedExtensions = isHostfix ? ".csv,.xlsx,text/csv" : ".xlsx";
  const hasTemplate = !isHostfix;

  const downloadTemplate = async () => {
    try {
      const r = await api.get(`${basePath}/template`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = downloadName;
      a.click(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onFileChange = (f) => {
    if (!f) return;
    if (isHostfix) {
      if (!f.name.match(/\.(csv|xlsx)$/i)) { toast.error("Solo file .csv o .xlsx"); return; }
    } else if (!f.name.match(/\.xlsx$/i)) { toast.error("Solo file .xlsx"); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("File troppo grande (max 10MB)"); return; }
    setFile(f);
  };

  const runPreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const r = await api.post(`${basePath}/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreviewData(r.data);
      setStep(2);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const runExecute = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const r = await api.post(`${basePath}/execute`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setExecuteData(r.data);
      setStep(3);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const filteredRows = !previewData ? [] : (previewData.rows || []).filter((r) => {
    if (filter === "all") return true;
    if (filter === "errors") return r.status === "error" || r.status === "error_permission_denied";
    if (filter === "updates") return r.status === "update";
    return true;
  });

  const sum = previewData?.summary || {};
  const processable = (sum.to_create || 0) + (sum.to_update || 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/20 backdrop-blur-[2px] flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-4xl max-h-[90vh] flex flex-col bg-background rounded-2xl border border-border shadow-2xl" data-testid="import-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border/60 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="h-5 w-5 text-lavender-500" />
            <div>
              <h2 className="font-display text-xl tracking-tight">{title}</h2>
              <div className="text-xs text-muted-foreground">Step {step}/3 · {step === 1 ? "Setup" : step === 2 ? "Anteprima" : "Esito"}</div>
            </div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-md hover:bg-accent flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === 1 && (
            <div className="space-y-5" data-testid="import-step-1">
              {hasTemplate ? (
                <div className="rounded-xl border border-border/60 bg-accent/20 p-4">
                  <div className="text-sm font-medium mb-1">1. Scarica il template Excel</div>
                  <p className="text-xs text-muted-foreground mb-3">Contiene gli header corretti, esempi compilati e dropdown di validazione.</p>
                  <Button onClick={downloadTemplate} variant="outline" data-testid="import-download-template" className="gap-2">
                    <Download className="h-4 w-4" /> Scarica template .xlsx
                  </Button>
                </div>
              ) : (
                <div className="rounded-xl border border-border/60 bg-accent/20 p-4">
                  <div className="text-sm font-medium mb-1">1. Usa il file CSV di HostFix Pro</div>
                  <p className="text-xs text-muted-foreground">
                    Carica direttamente il file <code>.csv</code> generato dal gestionale HostFix (separatore <code>;</code>, una riga per hotel). Il sistema riconosce i nomi degli hotel anche se differiscono leggermente (match fuzzy su token).
                  </p>
                </div>
              )}

              <div className="rounded-xl border-2 border-dashed border-border p-6 text-center hover:border-lavender-300 transition-colors cursor-pointer" onClick={() => inputRef.current?.click()} data-testid="import-dropzone">
                <input ref={inputRef} type="file" accept={acceptedExtensions} hidden onChange={(e) => onFileChange(e.target.files?.[0])} />
                <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                {file ? (
                  <div>
                    <div className="font-medium text-sm">{file.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">{(file.size / 1024).toFixed(1)} KB</div>
                  </div>
                ) : (
                  <div>
                    <div className="text-sm font-medium">{isHostfix ? "Trascina qui il file .csv (o .xlsx) o clicca per selezionare" : "Trascina qui il file .xlsx o clicca per selezionare"}</div>
                    <div className="text-xs text-muted-foreground mt-1">{isHostfix ? "Max 10 MB · max 10.000 righe" : isRoomsBulk ? "Max 10 MB · max 10.000 righe" : "Max 10 MB · max 5000 righe"}</div>
                  </div>
                )}
              </div>

              <div>
                <div className="text-sm font-medium mb-2">Modalità di import</div>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="mode" value="insert_only" checked={mode === "insert_only"} onChange={() => setMode("insert_only")} className="mt-1" data-testid="mode-insert" />
                    <div>
                      <div className="text-sm font-medium">Solo nuovi</div>
                      <div className="text-xs text-muted-foreground">I duplicati esistenti vengono saltati.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="mode" value="upsert" checked={mode === "upsert"} onChange={() => setMode("upsert")} className="mt-1" data-testid="mode-upsert" />
                    <div>
                      <div className="text-sm font-medium">Aggiorna esistenti</div>
                      <div className="text-xs text-muted-foreground">I record già presenti vengono aggiornati con i nuovi dati (i campi vuoti vengono ignorati).</div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {step === 2 && previewData && (
            <div className="space-y-4" data-testid="import-step-2">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { label: "Da creare", val: sum.to_create, icon: Plus, cls: "text-emerald-600" },
                  { label: "Da aggiornare", val: sum.to_update, icon: Pencil, cls: "text-sky-600" },
                  { label: "Saltati", val: sum.duplicates_skipped, icon: SkipForward, cls: "text-slate-500" },
                  { label: "Errori", val: (sum.errors || 0) + (sum.permission_denied || 0), icon: AlertTriangle, cls: "text-rose-600" },
                ].map((k) => (
                  <div key={k.label} className="rounded-xl border border-border/60 bg-background/40 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><k.icon className="h-3 w-3" /> {k.label}</div>
                    <div className={`font-display text-2xl font-medium mt-1 ${k.cls}`} data-testid={`kpi-${k.label.toLowerCase().replace(/ /g, "-")}`}>{k.val ?? 0}</div>
                  </div>
                ))}
              </div>
              {previewData.by_hotel && Object.keys(previewData.by_hotel).length > 0 && (
                <div className="rounded-xl border border-border/60 overflow-hidden" data-testid="by-hotel-breakdown">
                  <div className="px-3 py-2 bg-accent/30 text-[10px] uppercase tracking-wider text-muted-foreground">
                    Ripartizione per hotel
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/60">
                        <th className="px-3 py-1.5">Hotel</th>
                        <th className="px-3 py-1.5 text-right">Creazioni</th>
                        <th className="px-3 py-1.5 text-right">Aggiornamenti</th>
                        <th className="px-3 py-1.5 text-right">Saltati</th>
                        <th className="px-3 py-1.5 text-right">Errori</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(previewData.by_hotel).map(([name, b]) => (
                        <tr key={name} className="border-b border-border/40">
                          <td className="px-3 py-1.5 font-medium">{name}</td>
                          <td className="px-3 py-1.5 text-right text-emerald-600">{b.create || 0}</td>
                          <td className="px-3 py-1.5 text-right text-sky-600">{b.update || 0}</td>
                          <td className="px-3 py-1.5 text-right text-slate-500">{b.skip || 0}</td>
                          <td className="px-3 py-1.5 text-right text-rose-600">{b.errors || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">Filtra:</span>
                {[["all", "Tutto"], ["errors", "Solo errori"], ["updates", "Solo aggiornamenti"]].map(([v, lab]) => (
                  <button key={v} onClick={() => setFilter(v)} className={`px-3 py-1 rounded-full text-xs transition-colors ${filter === v ? "bg-lavender-100 text-lavender-700 dark:bg-lavender-900/40 dark:text-lavender-300" : "hover:bg-accent"}`}>{lab}</button>
                ))}
              </div>
              <div className="rounded-xl border border-border/60 overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-accent/30 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">#</th>
                        <th className="px-3 py-2 text-left">Stato</th>
                        <th className="px-3 py-2 text-left">Dati</th>
                        <th className="px-3 py-2 text-left">Messaggi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((r) => {
                        const m = STATUS_META[r.status] || STATUS_META.error;
                        return (
                          <tr key={r.row_number} className="border-t border-border/30" data-testid={`row-${r.row_number}`}>
                            <td className="px-3 py-2 tabular-nums">{r.row_number}</td>
                            <td className="px-3 py-2"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] ${m.cls}`}><m.icon className="h-3 w-3" />{m.label}</span></td>
                            <td className="px-3 py-2">
                              <div className="font-medium">{r.parsed.nome} {r.parsed.cognome || ""}</div>
                              <div className="text-muted-foreground">{r.parsed.citta || r.parsed.hotel_nome || ""} · {r.parsed.dipartimento || (r.parsed.categoria_stelle ? r.parsed.categoria_stelle + "★" : "")}</div>
                            </td>
                            <td className="px-3 py-2 text-rose-600 dark:text-rose-400">{r.errors?.length ? r.errors.join(" · ") : "—"}</td>
                          </tr>
                        );
                      })}
                      {filteredRows.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">Nessuna riga da mostrare con questo filtro.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {step === 3 && executeData && (
            <div className="space-y-4" data-testid="import-step-3">
              <div className={`rounded-xl border p-5 ${executeData.created + executeData.updated > 0 ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20" : "border-amber-300 bg-amber-50 dark:bg-amber-900/20"}`}>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                  <div>
                    <div className="font-display text-xl">Importazione completata</div>
                    <div className="text-sm text-muted-foreground mt-0.5">Durata: {executeData.duration_ms}ms</div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                  <div><span className="text-muted-foreground">Creati:</span> <span className="font-semibold text-emerald-600" data-testid="exec-created">{executeData.created}</span></div>
                  <div><span className="text-muted-foreground">Aggiornati:</span> <span className="font-semibold text-sky-600" data-testid="exec-updated">{executeData.updated}</span></div>
                  <div><span className="text-muted-foreground">Saltati:</span> <span className="font-semibold">{executeData.skipped}</span></div>
                  <div><span className="text-muted-foreground">Errori:</span> <span className="font-semibold text-rose-600">{(executeData.errors || 0) + (executeData.permission_denied || 0)}</span></div>
                </div>
              </div>
              {((executeData.errors || 0) + (executeData.permission_denied || 0)) > 0 && (
                <Button variant="outline" onClick={() => {
                  const errRows = (executeData.rows || []).filter((r) => r.status === "error" || r.status === "error_permission_denied");
                  if (!errRows.length) return;
                  const keys = Object.keys(errRows[0].parsed || {});
                  const header = ["row_number", "status", ...keys, "errors"];
                  const escape = (v) => { const s = v == null ? "" : String(v); return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
                  const lines = [header.join(";")];
                  for (const r of errRows) {
                    lines.push([r.row_number, r.status, ...keys.map((k) => escape(r.parsed?.[k])), escape((r.errors || []).join(" · "))].join(";"));
                  }
                  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  const ts = new Date().toISOString().slice(0, 10);
                  a.href = url; a.download = `errori-import-${entity === "hotels" ? "hotel" : "staff"}-${ts}.csv`;
                  a.click(); URL.revokeObjectURL(url);
                }} data-testid="download-errors-btn" className="gap-2">
                  <Download className="h-4 w-4" /> Scarica log errori (CSV)
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border/60 flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground">
            {step === 2 && previewData && <span>{processable} righe pronte all'importazione · {(previewData.rows || []).length || (previewData.actions_count ?? 0)} totali</span>}
          </div>
          <div className="flex gap-2">
            {step === 2 && <Button variant="outline" onClick={() => { setStep(1); setPreviewData(null); }}>Torna indietro</Button>}
            {step === 1 && (
              <Button onClick={runPreview} disabled={!file || loading} data-testid="import-verify-btn" className="gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Verifica file
              </Button>
            )}
            {step === 2 && (
              <Button onClick={runExecute} disabled={!processable || loading} data-testid="import-confirm-btn" className="gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Conferma importazione ({processable})
              </Button>
            )}
            {step === 3 && <Button onClick={() => { onComplete && onComplete(); onClose(); }} data-testid="import-close-btn">Chiudi e ricarica lista</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}
